import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { orderPendenciesApi } from '../../services/requests';
import requireSuccessfulResponse from '../../services/utilities/requireSuccessfulResponse';
import { OrderPendency, PendencyStatus } from '../../types/apiResponse/orderPendency';
import { ApiResPaginated } from '../../types/apiResponseTypes';
import checkPermission from '../../utils/checkPermission';
import { backendErrorNotification, frontendNotification } from '../Notification';
import * as s from './styles';
import Modal from '../Modal';

export type OrderApprovalProps = {
  orderNumber: number | string;
  onChange?: () => void;
  exportedErp?: string | null;
};

const statusKey: Record<PendencyStatus, string> = {
  pending: 'pendencyPending', approved: 'pendencyApproved', rejected: 'pendencyRejected',
};

function PendencyCard({ pendency, save, busy, note, setNote, exported }: {
  pendency: OrderPendency;
  save: (pendency: OrderPendency, note: string, status?: PendencyStatus) => Promise<void>;
  busy: boolean;
  note: string;
  setNote: (note: string) => void;
  exported: boolean;
}) {
  const { t } = useTranslation();
  const canEdit = checkPermission('orderPendency-edit');
  const decisionBlocked = exported || pendency.decision_blocked_reason === 'exported_order';
  const canDecide = canEdit && pendency.can_decide && !decisionBlocked;

  return (
    <s.Card $status={pendency.status}>
      <s.Meta>
        <strong>{t(statusKey[pendency.status])}</strong>
        <span>{pendency.order_item_id ? t('pendencyItem', { id: pendency.order_item_id }) : t('pendencyWholeOrder')}</span>
        <span>{t('pendencyCriticality', { level: pendency.criticality_level })}</span>
      </s.Meta>
      <h3>{pendency.description}</h3>
      {pendency.motive && <p><strong>{t('motive')}:</strong> {pendency.motive}</p>}
      <s.Meta>
        {pendency.create_by && <span>{t('pendencyCreatedBy')}: {pendency.create_by}</span>}
        <span>{t('pendencyCreatedAt')}: {pendency.created_at}</span>
        {pendency.approved_by && <span>{t('pendencyApprovedBy')}: {pendency.approved_by}</span>}
        {pendency.rejected_by && <span>{t('pendencyRejectedBy')}: {pendency.rejected_by}</span>}
      </s.Meta>
      <label htmlFor={`pendency-note-${pendency.id}`}>{t('pendencyNote')}</label>
      <s.NoteInput
        id={`pendency-note-${pendency.id}`}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        disabled={busy || !canEdit}
      />
      {canEdit && decisionBlocked && <s.Hint>{t('pendencyExportedOrderBlocked')}</s.Hint>}
      {canEdit && !pendency.can_decide && !decisionBlocked && <s.Hint>{t('pendencyAuthorityDenied')}</s.Hint>}
      {canEdit && (
        <s.Actions>
          <s.Action disabled={busy || note === (pendency.note ?? '')} onClick={() => void save(pendency, note)}>
            {t('pendencySaveNote')}
          </s.Action>
          <s.Action $tone="positive" disabled={busy || !canDecide || pendency.status === 'approved'}
            onClick={() => void save(pendency, note, 'approved')}>
            {t('pendencyApprove')}
          </s.Action>
          <s.Action $tone="negative" disabled={busy || !canDecide || pendency.status === 'rejected'}
            onClick={() => void save(pendency, note, 'rejected')}>
            {t('pendencyReject')}
          </s.Action>
        </s.Actions>
      )}
    </s.Card>
  );
}

// Conteudo independente do modal, reutilizavel em outras telas.
const OrderApproval = ({ orderNumber, onChange, exportedErp }: OrderApprovalProps) => {
  const { t } = useTranslation();
  const [pendencies, setPendencies] = useState<OrderPendency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [generalNoteOpen, setGeneralNoteOpen] = useState(false);
  const [generalNote, setGeneralNote] = useState('');
  const requestVersion = useRef(0);
  const exported = ['s', 'i'].includes((exportedErp ?? '').trim().toLowerCase());
  const canList = checkPermission('orderPendency-list');

  const load = useCallback(async (showLoading = true) => {
    const version = ++requestVersion.current;
    if (showLoading) setLoading(true);
    setError(false);
    try {
      // Percorre todas as paginas: pedidos podem ter mais de uma pagina de pendencias.
      const rows: OrderPendency[] = [];
      let page = 1;
      let totalPage = 1;
      do {
        orderPendenciesApi.query = `?orderNumber=${encodeURIComponent(orderNumber)}&page=${page}&quantityPerPage=50`;
        const response = await orderPendenciesApi.index();
        requireSuccessfulResponse(response);
        const result: ApiResPaginated<OrderPendency[]> = response.data;
        rows.push(...result.data);
        totalPage = result.paginated.totalPage;
        page += 1;
      } while (page <= totalPage && version === requestVersion.current);
      if (version === requestVersion.current) setPendencies(rows);
    } catch (err) {
      if (version === requestVersion.current) {
        setError(true);
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    setNotes({});
    setGeneralNoteOpen(false);
    if (canList) void load();
    return () => { requestVersion.current += 1; };
  }, [canList, load]);

  const save = async (pendency: OrderPendency, note: string, status?: PendencyStatus) => {
    if (saving) return;
    setSaving(true);
    try {
      orderPendenciesApi.query = '';
      const response = await orderPendenciesApi.update({
        note, ...(status ? { status } : {}), expected_status: pendency.status,
      }, pendency.id);
      requireSuccessfulResponse(response);
      setNotes((current) => { const next = { ...current }; delete next[pendency.id]; return next; });
      frontendNotification({ message: t('success'), type: 'success' });
      onChange?.();
      await load(false);
    } catch (err) {
      backendErrorNotification(err as AxiosError<any, any>);
      if ((err as AxiosError).response?.status === 409) await load(false);
    } finally {
      setSaving(false);
    }
  };

  if (!canList) return <p>{t('pendencyNoListPermission')}</p>;
  if (loading) return <p role="status">{t('pendencyLoading')}</p>;
  if (error && pendencies.length === 0) return <s.Action onClick={() => void load()}>{t('pendencyRetry')}</s.Action>;

  return (
    <section aria-label={t('orderApproval') as string}>
      {error && <p role="alert">{t('pendencyRefreshFailed')}</p>}
      <s.Toolbar>
        <s.Counts>
          <span>{t('pendencyTotal')}: <strong>{pendencies.length}</strong></span>
          {(['pending', 'approved', 'rejected'] as PendencyStatus[]).map((status) => (
            <span key={status}>
              {t(statusKey[status])}: <strong>{pendencies.filter((row) => row.status === status).length}</strong>
            </span>
          ))}
        </s.Counts>
        <s.Actions>
          {checkPermission('orderPendency-edit') && pendencies.length > 1 && (
            <s.Action disabled={saving} onClick={() => { setGeneralNote(''); setGeneralNoteOpen(true); }}>
              {t('pendencyGeneralNote')}
            </s.Action>
          )}
          <s.Action disabled={saving} onClick={() => void load(false)}>{t('pendencyReload')}</s.Action>
        </s.Actions>
      </s.Toolbar>
      {pendencies.length === 0 ? <p>{t('pendencyEmpty')}</p> : (
        <s.List>{pendencies.map((pendency) => (
          <PendencyCard key={pendency.id} pendency={pendency} save={save} busy={saving}
            note={notes[pendency.id] ?? pendency.note ?? ''} exported={exported}
            setNote={(note) => setNotes((current) => ({ ...current, [pendency.id]: note }))} />
        ))}</s.List>
      )}
      {generalNoteOpen && (
        <Modal showModal title={t('pendencyGeneralNote')} closeModal={() => setGeneralNoteOpen(false)}
          actionNameBtn={t('pendencyApplyGeneralNote')} disableActionBtn={!generalNote.trim() || saving}
          action={() => {
            setNotes((current) => ({ ...current, ...Object.fromEntries(
              pendencies.map((row) => [row.id, generalNote.trim()]),
            ) }));
            setGeneralNoteOpen(false);
          }}>
          <s.GeneralNoteContent>
            <p>{t('pendencyGeneralNoteDescription')}</p>
            <label htmlFor="general-pendency-note">{t('pendencyNote')}</label>
            <s.NoteInput id="general-pendency-note" value={generalNote}
              onChange={(event) => setGeneralNote(event.target.value)} rows={4} />
          </s.GeneralNoteContent>
        </Modal>
      )}
    </section>
  );
};

export default OrderApproval;
